"use client";

/**
 * IconButton - Shared icon-only button for SOLA tool displays
 *
 * Styled with design tokens since @/ui Button lacks icon-only mode.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { colors, radius, spacing } from "@/ui/tokens";

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  size?: "sm" | "md";
  color?: "default" | "inherit";
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, size = "sm", color = "default", style, ...props }, ref) => {
    const dim = size === "sm" ? 28 : 34;

    return (
      <button
        ref={ref}
        type="button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: dim,
          height: dim,
          padding: 0,
          border: "none",
          borderRadius: radius.md,
          background: "transparent",
          color: color === "inherit" ? "inherit" : colors.text.muted,
          cursor: "pointer",
          flexShrink: 0,
          transition: "background-color 150ms ease",
          ...style,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor =
            colors.action.productLight;
          props.onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor =
            "transparent";
          props.onMouseLeave?.(e);
        }}
        {...props}
      >
        {children}
      </button>
    );
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
